import {
  Button,
  Col,
  Empty,
  List,
  Modal,
  Row,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { t } from 'helpers/i18n';
import { notificationHooks } from 'hooks';
import React from 'react';
import NotificationItem from './NotificationItem';
import { parseNotificationDeeplink } from './utils';
import { trackingConstants } from 'constants/index';

const { useNotificationHistory } = notificationHooks;
const {
  TRACKING_CONTENT_REGION_NAME,
  TRACKING_CONTENT_NAME,
} = trackingConstants;

interface NotificationModalProps {
  open: boolean;
  onClose: () => void;
  appId?: string;
  unreadCount: number;
  // Resync the bell badge after read-state changes made inside the modal.
  onAfterChange: () => void;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  open,
  onClose,
  appId,
  unreadCount,
  onAfterChange,
}) => {
  const {
    items,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    markRead,
    markReadAll,
  } = useNotificationHistory(appId, open, onAfterChange);

  const title = (
    <Row justify="space-between" align="middle" wrap={false}>
      <Col>
        <Typography.Text strong style={{ fontSize: 16 }}>
          {t('Notifications.Title')}
        </Typography.Text>
        {unreadCount > 0 && (
          <Tag color="red" style={{ marginLeft: 8 }}>
            {t('Notifications.NewCount', { count: unreadCount })}
          </Tag>
        )}
      </Col>
      <Col>
        <Typography.Link
          data-testid="notification-modal-mark-all-read"
          data-track-content
          data-content-region-name={
            TRACKING_CONTENT_REGION_NAME.NotificationModal
          }
          data-content-name={TRACKING_CONTENT_NAME.MarkAllRead}
          data-content-payload={JSON.stringify({ unreadCount })}
          style={{ fontSize: 14 }}
          disabled={unreadCount === 0}
          onClick={markReadAll}
        >
          {t('Notifications.MarkAllAsRead')}
        </Typography.Link>
      </Col>
    </Row>
  );

  const renderFooter = () => {
    if (!items.length) return null;
    if (loadingMore) {
      return (
        <div className="notification-modal-more">
          <Spin size="small" />
        </div>
      );
    }
    if (hasMore) {
      return (
        <Button
          type="link"
          block
          data-testid="notification-modal-load-more"
          onClick={loadMore}
        >
          {t('Notifications.LoadMore')}
        </Button>
      );
    }
    return (
      <div className="notification-modal-more">
        <Typography.Text type="secondary">
          {t('Notifications.NoMore')}
        </Typography.Text>
      </div>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="notification-modal-status">
          <Spin />
        </div>
      );
    }
    if (!items.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('Notifications.Empty')}
          className="notification-modal-status"
        />
      );
    }
    return (
      <>
        <List
          dataSource={items}
          renderItem={notification => (
            <List.Item
              key={notification.id}
              className={notification.is_read ? undefined : 'unread'}
              data-track-content
              data-content-region-name={
                TRACKING_CONTENT_REGION_NAME.NotificationModal
              }
              data-content-name={TRACKING_CONTENT_NAME.NotificationItem}
              data-content-payload={JSON.stringify({
                id: notification.id,
                isRead: notification.is_read,
                hasDeeplink: !!parseNotificationDeeplink(notification),
              })}
              onClick={() => markRead(notification)}
            >
              <NotificationItem
                notification={notification}
                onMarkRead={markRead}
                onAfterNavigate={onClose}
                trackingRegionName={
                  TRACKING_CONTENT_REGION_NAME.NotificationModal
                }
              />
            </List.Item>
          )}
        />
        {renderFooter()}
      </>
    );
  };

  return (
    <Modal
      centered
      destroyOnClose
      title={title}
      visible={open}
      footer={null}
      width={520}
      className="notification-modal"
      onCancel={onClose}
    >
      <div className="notification-modal-body">{renderBody()}</div>
    </Modal>
  );
};

export default NotificationModal;
